import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import { classes } from 'html-classes';
import * as React from 'react';

import styles from './Carousel.module.scss';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  className?: string;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

const useCarousel = () => {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
};

const Carousel: React.FC<React.ComponentProps<'div'> & CarouselProps> = ({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  ...props
}) => {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) {
      return;
    }
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) {
      return;
    }
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) {
      return;
    }
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api,
        opts,
        orientation:
          orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        {...props}
        onKeyDownCapture={handleKeyDown}
        className={classes([styles.carousel, className])}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
      />
    </CarouselContext.Provider>
  );
};

const CarouselContent: React.FC<
  Omit<React.ComponentProps<'div'>, 'className'> & { className?: string }
> = ({ className, ...props }) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className={classes([styles.carouselContent, className])}
      data-slot="carousel-content"
    >
      <div
        {...props}
        className={classes([
          styles.carouselViewport,
          orientation === 'horizontal' ? styles.horizontal : styles.vertical,
        ])}
      />
    </div>
  );
};

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentProps<'div'>, 'className'> & { className?: string }
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel();

  return (
    <div
      ref={ref}
      {...props}
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={classes([
        styles.carouselItem,
        orientation === 'horizontal' ? styles.horizontal : styles.vertical,
        className,
      ])}
    />
  );
});

CarouselItem.displayName = 'CarouselItem';

export {
  type CarouselApi,
  type CarouselOptions,
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
};
